/**
 * 柜子是干货还是冷冻 —— 一个地方说了算。
 *
 * ── 为什么要有这个文件 ──
 *
 * 界面上柜子那一格画的是 `船公司 · 尺寸代码 · 干货/冷冻`，而那第三格一直是
 * 「不是 reefer 就写 Dry」。问题在于「不知道」也不是 reefer，于是**没有值的时候
 * 界面照样理直气壮写一个 Dry**。出口单在提柜之前根本没有柜子记录，这一项永远
 * 是空的 —— 线上这个租户 796 只柜子里有 165 只是空的，全被画成了 Dry。
 *
 * 更难看的是它和紧挨着的那个代码互相矛盾：`20RF · Dry`。RF 就是冷冻柜，
 * 答案本来就写在左边那一格里，只是这两处从来不互相看一眼。
 *
 * ── 规矩 ──
 *
 * 1. **两边有一边说冷冻，就是冷冻。** 不是各打五十大板：
 *    · 代码里的 R（RF/RH）是柜子自己的规格，明确；
 *    · 而那个词的默认值历来就是 `dry` —— 一个「干货」多半只是没人说过，
 *      一个「冷冻」却是有人或有哪条路**明确说过**。拿前者盖掉后者，
 *      就是拿沉默盖掉证言。
 *    错判的代价也不对称：冷柜当干柜派出去＝到了码头才发现要插电、要发电机、
 *    预约窗口还不一样；反过来只是白紧张一场。
 * 2. **两边都只说干货，才写干货。**
 * 3. **谁都说不出来就什么都不画。** 宁可空着让人去查，也别编一个看着像真的 ——
 *    空值会让人去核，假值会被当成事实读下去。
 *
 * 认不出来的代码（开顶 40OT、框架 40FR、罐式 20TK）自己不投票，听那个词的；
 * 词也没有就走第 3 条 —— 它们既不是干货也不是冷冻，硬塞进两档里只会说错话。
 */

export type ContainerKind = 'dry' | 'reefer'

/** 冷冻：RF 冷冻、RH 冷冻高柜、RE/RT 带机组的几种写法。40RFHC / 40RHHQ 是旧写法。 */
const REEFER_CODE = /^\d{2}(RF|RH|RE|RT)/

/**
 * 干货：GP/G1 通用、DC/DH/DV 干货（含高柜）、HC/HQ 高柜、ST/SD 标准。
 *
 * ⚠️ 不能写成「不是 R 开头就算干货」—— `40ft` `45ft` 这种粗尺寸的第三个字符是 F，
 * 一律当干货就等于把「不知道」又说成了 Dry，这个文件存在的意义就没了。
 */
const DRY_CODE = /^\d{2}(GP|G1|DC|DH|DV|HC|HQ|ST|SD)/

/**
 * 这一格到底该画什么。
 *
 * @param sizeCode   旁边那一格的尺寸/ISO 代码（`20RF` / `40HC` / `40ft` / 空）
 * @param storedKind 库里存着的那个词（可能没有，也可能和代码打架）
 * @returns 'reefer' | 'dry'；判不出来返回 null —— 调用方**不要**给它兜底
 */
export function resolveContainerKind(
  sizeCode?: string | null,
  storedKind?: string | null,
): ContainerKind | null {
  const code = String(sizeCode ?? '').trim().toUpperCase().replace(/\s+/g, '')
  const stored = String(storedKind ?? '').trim().toLowerCase()

  // 任一边说冷冻就是冷冻 —— 这一条必须排在「代码说干货」前面，
  // 否则 `40HQ` 配上一个明确的 reefer 又会被读成干货。
  if (REEFER_CODE.test(code) || stored === 'reefer') return 'reefer'

  if (DRY_CODE.test(code) || stored === 'dry') return 'dry'

  return null
}

/** 同一个判断，直接给屏幕上那两个词。判不出来返回 null，那一格连同分隔点一起不画。 */
export function containerKindLabel(
  sizeCode?: string | null,
  storedKind?: string | null,
): 'Reefer' | 'Dry' | null {
  const kind = resolveContainerKind(sizeCode, storedKind)
  return kind === 'reefer' ? 'Reefer' : kind === 'dry' ? 'Dry' : null
}
